import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import type { Components, Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import * as React from 'react';
import { typographyVariantMappings } from './createTypography';

const cellHorDecor = 4; // simplifi style edit cell decor
const cellHorPadding = 10;

/**
 *
 * @param theme - this is the global theme w/o MUI components
 * @returns MUI component structure, pass to create stream in components object
 *
 * Note: You can use MUI theme in styleOverrides by providing a function for the key value which takes
 *
 */
const CreateMuiComponents = (theme: Theme): Components<Theme> => {
  const { palette } = theme;
  return {
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3),
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3),
          '&:last-child': {
            paddingBottom: theme.spacing(3),
          },
        },
      },
    },
    MuiAvatar: {
      variants: [
        {
          props: { variant: 'rounded' },
          style: {
            border: `1px solid ${theme.palette.border.light}`,
            borderRadius: 8,
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
          },
        },
      ],
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          color: theme.palette.success.dark,
          backgroundColor: theme.palette.success.light,
        },
        standardInfo: {
          color: theme.palette.info.dark,
          backgroundColor: theme.palette.info.light,
        },
        standardWarning: {
          color: theme.palette.warning.dark,
          backgroundColor: theme.palette.warning.light,
        },
        standardError: {
          color: theme.palette.error.dark,
          backgroundColor: theme.palette.error.light,
        },
        filledSuccess: {
          color: theme.palette.success.contrastText,
          backgroundColor: theme.palette.success.main,
        },
        filledInfo: {
          color: theme.palette.info.contrastText,
          backgroundColor: theme.palette.info.main,
        },
        filledWarning: {
          color: theme.palette.warning.contrastText,
          backgroundColor: theme.palette.warning.main,
        },
        filledError: {
          color: theme.palette.error.contrastText,
          backgroundColor: theme.palette.error.main,
        },
        outlinedSuccess: {
          color: theme.palette.success.dark,
          borderColor: theme.palette.success.main,
        },
        outlinedInfo: {
          color: theme.palette.info.dark,
          borderColor: theme.palette.info.main,
        },
        outlinedWarning: {
          color: theme.palette.warning.dark,
          borderColor: theme.palette.warning.main,
        },
        outlinedError: {
          color: theme.palette.error.dark,
          borderColor: theme.palette.error.main,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderColor: 'transparent',
          '--DataGrid-containerBackground': theme.palette.background.paper,
          '--DataGrid-pinnedBackground': theme.palette.background.paper,
          '--DataGrid-rowBorderColor': theme.palette.border.light,
          '.MuiDataGrid-filler--pinnedLeft': {
            borderRight: 'none',
          },
          '.MuiDataGrid-filler--pinnedRight': {
            borderLeft: 'none',
          },
        },
        columnSeparator: {
          visibility: 'hidden',
          color: theme.palette.divider,
        },
        columnHeader: {
          fontWeight: 500,
          padding: `0 ${cellHorPadding + cellHorDecor}px`,
          borderBottom: `1px ${theme.palette.border.main} solid !important`,
          '&:focus, &:focus-within': {
            outline: 'none',
          },
          '&:hover': {
            '.MuiDataGrid-columnSeparator': {
              visibility: 'visible',
            },
          },
          '&.MuiDataGrid-columnHeader--withLeftBorder': {
            borderLeft: `1px ${theme.palette.border.main} solid`,
          },
          '&.MuiDataGrid-columnHeader--withRightBorder': {
            borderRight: `1px ${theme.palette.border.main} solid`,
          },
        },
        columnHeaderCheckbox: {
          padding: `0px !important`,
        },
        row: {
          '&:not(:hover).MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'transparent',
          },
        },
        cell: {
          '&.MuiDataGrid-cell': {
            '&:focus': {
              outline: 'none',
            },
            padding: `0 ${cellHorPadding + cellHorDecor}px`,
          },
          '&.MuiDataGrid-cell--editing': {
            boxShadow: 'none',
          },
          '&.MuiDataGrid-cell--withLeftBorder': {
            borderLeft: `1px ${theme.palette.border.main} solid`,
          },
          '&.MuiDataGrid-cell--withRightBorder': {
            borderRight: `1px ${theme.palette.border.main} solid`,
          },
          '&.MuiDataGrid-cell--editable': {
            backgroundColor: theme.palette.background.paper,
            borderColor: 'transparent',
            borderRadius: 4,
            margin: `8px ${cellHorDecor}px`,
            padding: `0 ${cellHorPadding}px !important`, // should override MuiDataGrid-cell padding
            height: 'auto',
            display: 'flex',
            alignItems: 'center',
            minWidth: `calc(var(--width) - ${cellHorDecor * 2}px)`,
            maxWidth: `calc(var(--width) - ${cellHorDecor * 2}px)`,
          },
        },
        rowReorderCell: {
          '&.MuiDataGrid-rowReorderCell--draggable': {
            color: theme.palette.icon.main,
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ArrowDropDownRoundedIcon />,
      },
      styleOverrides: {
        endAdornment: {
          marginRight: 6,
        },
        listbox: {
          padding: 8,
        },
        option: {
          borderRadius: 8,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          position: 'absolute',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0,0,0,0.74)',
        },
        invisible: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        firstButton: {
          borderStyle: 'hidden',
          borderRadius: 'inherit',
          margin: 2,
        },
        middleButton: {
          borderStyle: 'hidden',
          borderRadius: 'inherit',
          margin: 2,
        },
        lastButton: {
          borderStyle: 'hidden',
          borderRadius: 'inherit',
          margin: 2,
        },
      },
    },
    MuiIconButton: {
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            border: '1px solid',
            color: theme.palette.text.primary,
            background: theme.palette.background.paper,
            borderColor: theme.palette.border.main,
            borderRadius: 8,
            '&:hover': {
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          },
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true, // No more ripple, on the whole application 💣!
        size: 'medium',
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'add' },
          style: {},
        },
        {
          props: { variant: 'text' },
          style: {},
        },
        {
          props: { variant: 'contained' },
          style: {
            color: theme.palette.text.contrastText,
            '&.Mui-disabled': {
              // make disabled contained button faded main color instead of grey
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.contrastText,
              opacity: 0.45,
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === 'small' && {
            borderRadius: 6,
            ...theme.typography.paragraphMedium,
          }),
          ...(ownerState.size === 'medium' && {
            borderRadius: 8,
            ...theme.typography.paragraphMedium,
          }),
          ...(ownerState.size === 'large' && {
            borderRadius: 12,
            ...theme.typography.bodyMedium,
          }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              color: theme.palette.text.primary,
              background: theme.palette.background.paper,
              borderColor: theme.palette.border.main,
              '&:hover': {
                color: theme.palette.primary.main,
                background: theme.palette.primary.light,
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Defining new button variant
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'primary' && {
              color: theme.palette.primary.main,
              background: theme.palette.primary.light,
              '&:hover': {
                color: theme.palette.primary.dark,
                background: theme.palette.action.selected,
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          textTransform: 'capitalize',
          textWrap: 'nowrap',
          '@media print': {
            display: 'none',
          },
        }),
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        legend: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          height: '22px',
          borderRadius: '6px',
        },
        sizeMedium: {
          height: '28px',
          borderRadius: '8px',
        },
        labelSmall: {
          ...theme.typography.captionRegular,
        },
        label: {
          ...theme.typography.paragraphRegular,
        },
      },
      variants: [
        {
          props: { variant: 'tonal' },
          style: {},
        },
        {
          props: { variant: 'tonal', color: 'primary' },
          style: {
            '&.MuiChip-colorPrimary': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.dark,
            },
            '& .MuiChip-iconColorPrimary': {
              color: theme.palette.primary.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'secondary' },
          style: {
            '&.MuiChip-colorSecondary': {
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.secondary.dark,
            },
            '& .MuiChip-iconColorSecondary': {
              color: theme.palette.secondary.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'success' },
          style: {
            '&.MuiChip-colorSuccess': {
              backgroundColor: theme.palette.success.light,
              color: theme.palette.success.dark,
            },
            '& .MuiChip-iconColorSuccess': {
              color: theme.palette.success.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'info' },
          style: {
            '&.MuiChip-colorInfo': {
              backgroundColor: theme.palette.info.light,
              color: theme.palette.info.dark,
            },
            '& .MuiChip-iconColorInfo': {
              color: theme.palette.info.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'warning' },
          style: {
            '&.MuiChip-colorWarning': {
              backgroundColor: theme.palette.warning.light,
              color: theme.palette.warning.dark,
            },
            '& .MuiChip-iconColorWarning': {
              color: theme.palette.warning.dark,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'error' },
          style: {
            '&.MuiChip-colorError': {
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.dark,
            },
            '& .MuiChip-iconColorError': {
              color: theme.palette.error.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'inactive' },
          style: {
            '&.MuiChip-colorInactive': {
              backgroundColor: theme.palette.inactive.light,
              color: theme.palette.inactive.dark,
            },
            '& .MuiChip-iconColorInactive': {
              color: theme.palette.inactive.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'active' },
          style: {
            '&.MuiChip-colorActive': {
              backgroundColor: theme.palette.active.light,
              color: theme.palette.active.dark,
            },
            '& .MuiChip-iconColorActive': {
              color: theme.palette.active.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'upcoming' },
          style: {
            '&.MuiChip-colorUpcoming': {
              backgroundColor: theme.palette.upcoming.light,
              color: theme.palette.upcoming.dark,
            },
            '& .MuiChip-iconColorUpcoming': {
              color: theme.palette.upcoming.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'critical' },
          style: {
            '&.MuiChip-colorCritical': {
              backgroundColor: theme.palette.critical.light,
              color: theme.palette.critical.dark,
            },
            '& .MuiChip-iconColorCritical': {
              color: theme.palette.critical.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'pending' },
          style: {
            '&.MuiChip-colorPending': {
              backgroundColor: theme.palette.pending.light,
              color: theme.palette.pending.dark,
            },
            '& .MuiChip-iconColorPending': {
              color: theme.palette.pending.main,
            },
          },
        },
      ],
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          borderRadius: 12,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${theme.palette.border.light}`,
        },
        spacing: {
          padding: theme.spacing(3),
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {},
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3),
          borderBottom: `1px solid ${theme.palette.border.light}`,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        contained: {
          marginLeft: 12,
          marginRight: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          display: 'none',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          letterSpacing: '0.01071em',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        readOnly: {
          '&$readOnly': {
            background: 'none',
            border: 'none',
          },
        },
      },
      defaultProps: {
        inputProps: {
          autoCorrect: 'off',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          display: 'none',
        },
        outlined: {
          '&$shrink': {
            backgroundColor: theme.palette.mode !== 'dark' ? '#ffffff' : theme.palette.background.default,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        padding: {
          padding: 8,
          '& > li': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiModal: {
      defaultProps: {
        // This is because if any dialog / popover / menu is open, the user cannot type into Intercom. This has the side
        // effect of potentially losing focus for the modal, which affects accessibility. If we decide to remove this,
        // please consider all things
        disableEnforceFocus: true,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${palette.border.light}`,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: theme.palette.background.default,
          border: `1px solid ${palette.border.light}`,
          // alignItems: 'baseline', // it breaks input adornments
          '&.Mui-focused': {
            background: theme.palette.background.paper,
            border: `1px solid ${palette.primary.main}`,
            boxShadow: `0px 0px 0px 4px ${palette.primary.light}`,
          },
          '&:hover': {
            background: theme.palette.background.paper,
            border: `1px solid ${palette.primary.main}`,
          },
          '&:before, &:after, &:hover:not(.Mui-disabled):before, &:hover:not(.Mui-disabled):after': {
            borderBottom: 0,
          },
          '&.Mui-disabled': {
            background: theme.palette.background.default,
            borderColor: theme.palette.border.light,
            color: theme.palette.text.disabled,
          },
          '&.Mui-error': {
            background: theme.palette.background.default,
            border: `1px solid ${palette.error.main}`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '&:hover fieldset': {
            borderColor: theme.palette.action.hover,
          },
        },
        notchedOutline: {
          borderColor: theme.palette.border.main,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: theme.palette.icon.main,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderColor: theme.palette.border.light,
        },
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
      },
      defaultProps: {
        elevation: 8, // set to elevation 8 because right now menu/popover elevation aren't applying over the default '1' on paper
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          border: `1px solid ${palette.border.light}`,
        },
      },
    },

    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          ...theme.typography.bodyRegular,
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          ...theme.typography.bodyRegular,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        // variant: 'filled',
        IconComponent: ArrowDropDownRoundedIcon,
      },
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
        },
        icon: {
          color: theme.palette.icon.main,
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'pulse',
      },
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.skeleton.dark,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: theme.palette.text.secondary,
        },
        textColorPrimary: {
          ...(palette.mode === 'dark' && {
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          background: theme.palette.background.paper,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
        inputProps: {
          'data-1p-ignore': 'true',
        },
      },
      styleOverrides: {
        root: {
          '&.subvariant-plain': {
            verticalAlign: 'middle',
            '& .MuiAutocomplete-endAdornment': {
              marginRight: '0px',
            },
            '& .MuiAutocomplete-inputRoot:before, .MuiAutocomplete-inputRoot:after': {
              borderBottom: 'none !important',
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.border.light}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.text.primary,
          color: theme.palette.background.paper,
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1.5),
          padding: theme.spacing(2),
          ...theme.typography.paragraphRegular,
        },
        arrow: {
          color: theme.palette.text.primary,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {},
      },
      defaultProps: {
        variant: 'bodyRegular',
        variantMapping: typographyVariantMappings,
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 40,
          height: 24,
          padding: 0,
          margin: 9,
        },
        switchBase: {
          padding: 3,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: theme.palette.background.paper,
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: theme.palette.action.disabledOpacity,
              cursor: 'not-allowed',
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: theme.palette.primary.main,
            border: `6px solid ${palette.background.layer2}`,
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.action.disabledOpacity,
            cursor: 'not-allowed',
          },
        },
        thumb: {
          boxSizing: 'border-box',
          width: 18,
          height: 18,
          color: theme.palette.common.white,
        },
        track: {
          borderRadius: 12,
          backgroundColor: theme.palette.background.layer2,
          opacity: 1,
          transition: theme.transitions.create(['background-color'], {
            duration: 500,
          }),
        },
      },
    },
  };
};

export default CreateMuiComponents;
